import React, { useEffect, useRef, useState } from "react";
import { Skeleton } from "@mui/material";
import { useTranslation } from "@helpers/translate";
import TradeArrowDownIcon from "@assets/icons/TradeArrowDownIcon";
import TradeArrowUpIcon from "@assets/icons/TradeArrowUpIcon";
import { formatDollarPrice } from "../../../../Orders/positionFormat";
import {
  CANDLES_STATUS,
  CHART_VIEW,
  ENTRY_BADGE_RADIUS,
} from "../../constants";
import { drawCandles } from "../../utils/drawCandles";
import { drawLine } from "../../utils/drawLine";
import {
  AXIS_WIDTH,
  getCandleIndexAtX,
  getLineIndexAtX,
  isTimeBasedLine,
} from "../../utils/chartFrame";
import "./TradeReplayChart.sass";

const ENTRY_ICON_SIZE = 12;

const isSamePoint = (first, second) =>
  first?.x === second?.x && first?.y === second?.y;

/** CSS size of the canvas, follows the modal width */
const useElementSize = (ref) => {
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const element = ref.current;

    if (!element) return undefined;

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;

      setSize({ width, height });
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, [ref]);

  return size;
};

/**
 * Candles of the trade played from the entry, or why there are none.
 *
 * @param {object} props
 * @param {string} props.view CHART_VIEW.LINE or CHART_VIEW.CANDLES
 * @param {string} props.status CANDLES_STATUS
 * @param {object[]} props.candles shown so far
 * @param {object[]} props.rangeCandles all candles of the trade
 * @param {number} props.revealed candle points of the line drawn, fractional
 * @param {number|null} props.entryPrice
 * @param {number|null} props.exitPrice of a closed trade
 * @param {number|null} props.entryTime open time of the trade, ms
 * @param {number|null} props.exitTime close time of a closed trade, ms
 * @param {number|null} props.intervalMs candle interval, ms
 * @param {boolean} props.isDone the replay has played
 * @param {boolean} props.isProfit
 * @param {boolean} props.isLong direction of the badge on the entry line
 * @param {{index: number, price: number, label: string}|null} props.hover candle under the pointer
 * @param {(index: number|null) => void} props.onHoverChange
 * @param {() => void} props.onRetry
 */
export const TradeReplayChart = ({
  view,
  status,
  candles,
  rangeCandles,
  revealed,
  entryPrice,
  exitPrice,
  entryTime,
  exitTime,
  intervalMs,
  isDone,
  isProfit,
  isLong,
  hover,
  onHoverChange,
  onRetry,
}) => {
  const { __ } = useTranslation();
  const canvasRef = useRef(null);
  const { width, height } = useElementSize(canvasRef);
  const [entryPoint, setEntryPoint] = useState(null);
  const EntryIcon = isLong ? TradeArrowUpIcon : TradeArrowDownIcon;

  useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas || !width || !height) return;

    const pixelRatio = window.devicePixelRatio || 1;
    const ctx = canvas.getContext("2d");

    canvas.width = Math.round(width * pixelRatio);
    canvas.height = Math.round(height * pixelRatio);
    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const draw = view === CHART_VIEW.LINE ? drawLine : drawCandles;
    let point = null;

    if (rangeCandles.length) {
      point = draw(ctx, {
        width,
        height,
        candles,
        rangeCandles,
        revealed,
        entryPrice,
        exitPrice,
        entryTime,
        exitTime,
        intervalMs,
        isDone,
        isProfit,
        hover,
        formatPrice: formatDollarPrice,
      });
    }

    // Drawn every frame of the replay, the badge moves only with the line
    setEntryPoint((previous) =>
      isSamePoint(previous, point) ? previous : point,
    );
  }, [
    view,
    width,
    height,
    candles,
    rangeCandles,
    revealed,
    entryPrice,
    exitPrice,
    entryTime,
    exitTime,
    intervalMs,
    isDone,
    isProfit,
    hover,
  ]);

  const handlePointerMove = (event) => {
    if (!rangeCandles.length) return;

    const x = event.clientX - event.currentTarget.getBoundingClientRect().left;
    const plotWidth = width - AXIS_WIDTH;

    onHoverChange(
      isTimeBasedLine({ view, exitPrice, entryTime, exitTime, intervalMs })
        ? getLineIndexAtX(x, {
          rangeCandles,
          intervalMs,
          entryTime,
          exitTime,
          plotWidth,
        })
        : getCandleIndexAtX(view, x, plotWidth, rangeCandles.length),
    );
  };

  return (
    <div className="trade-replay-chart">
      <canvas
        ref={canvasRef}
        className="trade-replay-chart__canvas"
        aria-hidden="true"
        onPointerMove={handlePointerMove}
        onPointerDown={handlePointerMove}
        onPointerLeave={() => onHoverChange(null)}
      />

      {entryPoint && (
        <span
          className={`trade-replay-chart__entry trade-replay-chart__entry--${
            isLong ? "up" : "down"
          }`}
          style={{
            left: Math.max(entryPoint.x, ENTRY_BADGE_RADIUS),
            top: entryPoint.y,
          }}
          aria-hidden="true"
        >
          <EntryIcon size={ENTRY_ICON_SIZE} color="currentColor" />
        </span>
      )}

      {status === CANDLES_STATUS.LOADING && (
        <Skeleton
          variant="rounded"
          className="trade-replay-chart__overlay trade-replay-chart__skeleton"
        />
      )}

      {status === CANDLES_STATUS.EMPTY && (
        <p className="trade-replay-chart__overlay trade-replay-chart__message">
          {__("futures.trade_details.chart_no_data")}
        </p>
      )}

      {status === CANDLES_STATUS.ERROR && (
        <div className="trade-replay-chart__overlay trade-replay-chart__message">
          <p>{__("futures.trade_details.chart_error")}</p>
          <button
            type="button"
            className="trade-replay-chart__retry"
            onClick={onRetry}
          >
            {__("futures.trade_details.chart_retry")}
          </button>
        </div>
      )}
    </div>
  );
};
